import React from "react";
import { Banner, Stack, ButtonTextLink } from "@chaser/components";
import useBeAbleToStartChase from "../../hooks/useBeAbleToStartChase";
import { useHistory } from "react-router";
import { useRouteMatch } from "react-router-dom";
import { BillingProvider, PlanFeatures } from "../../util/constants";

const OVER_LIMIT_LABELS = {
  [PlanFeatures.TEMPLATES]: "templates",
  [PlanFeatures.SCHEDULES]: "schedules",
  [PlanFeatures.USERS]: "users",
  [PlanFeatures.EMAIL_INTEGRATION_CONNECTIONS]: "email integration connections",
};

const OVER_LIMIT_ROUTE = {
  [PlanFeatures.USERS]:
    "/settings/subscription/addons?module=organisation-module",
  [PlanFeatures.TEMPLATES]:
    "/settings/subscription/addons?module=receivables-module",
  [PlanFeatures.SCHEDULES]:
    "/settings/subscription/addons?module=receivables-module",
  [PlanFeatures.EMAIL_INTEGRATION_CONNECTIONS]:
    "/settings/subscription/addons?module=receivables-module",
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const isUsersOverLimit = (addon = {}) => {
  if (addon?.isUnlimitedUsersLimit) return false;

  const totalUsed = toNumber(addon?.totalUsed ?? addon?.overages);
  const totalUsersLimit = toNumber(
    addon?.totalUsersLimit ||
      addon?.planLimit ||
      toNumber(addon?.totalIncludedUsers) +
        toNumber(addon?.totalPurchasedUsers),
  );

  if (totalUsersLimit > 0) {
    return totalUsed > totalUsersLimit;
  }

  return toNumber(addon?.overLimits) > 0;
};

const getOverLimitFeatureLabels = (addons = {}) => {
  const hasTemplateOverLimit = Object.values(
    addons?.[PlanFeatures.TEMPLATES]?.sharedLimits || {},
  ).some((entry) => Number(entry?.overLimits || 0) > 0);

  return Object.entries(OVER_LIMIT_LABELS)
    .filter(([feature]) => {
      if (feature === PlanFeatures.TEMPLATES) {
        return hasTemplateOverLimit;
      }

      if (feature === PlanFeatures.USERS) {
        return isUsersOverLimit(addons?.[feature]);
      }

      return Number(addons?.[feature]?.overLimits || 0) > 0;
    })
    .map(([feature, label]) => ({
      feature,
      label,
      route: OVER_LIMIT_ROUTE[feature],
    }));
};

const toSummaryText = (labels = []) => {
  if (!labels.length)
    return "Some receivables add-ons are over your plan limits.";
  if (labels.length === 1) {
    return `Your receivables ${labels[0]} are over your plan limits.`;
  }

  const head = labels.slice(0, -1).join(", ");
  const tail = labels[labels.length - 1];
  return `Your receivables ${head} and ${tail} are over your plan limits.`;
};

const PlanUpgradeBanner = () => {
  const history = useHistory();
  const data = useBeAbleToStartChase();

  const validRoutes = useRouteMatch({
    path: [
      "/",
      "/receivables",
      "/receivables/*",
      "/manage",
      "/manage/*",
      "/dashboard",
      "/dashboard/*",
      "/settings/*",
      "/collections",
      "/credit-check",
      "/reports",
      "/chaserpay",
    ],
    exact: true,
  });

  if (!validRoutes) return null;
  if (data.isInTrial) return null;
  if (data.canStartChase) return null;

  const overLimitFeatures = getOverLimitFeatureLabels(data?.plan?.addons || {});
  if (!overLimitFeatures.length) return null;

  const overLimitLabels = overLimitFeatures.map(({ label }) => label);
  const primaryOverLimitRoute = overLimitFeatures[0]?.route;

  return (
    <Stack>
      <Banner intent="danger" showCloseButton={false}>
        <span>
          {toSummaryText(overLimitLabels)}{" "}
          <ButtonTextLink
            onClick={() => {
              if (primaryOverLimitRoute) {
                history.push(primaryOverLimitRoute);
              }
            }}
            monochrome
          >
            Open Plans & add-ons
          </ButtonTextLink>
          {data.subscriptionService !== BillingProvider.XERO
            ? " to add capacity and continue chasing without interruption."
            : " to review your usage and reduce over-limit items."}
        </span>
      </Banner>
    </Stack>
  );
};

export default PlanUpgradeBanner;
